import { useTranslate } from "@tolgee/react";
import { Button } from "#/components/ui/button";
import { useWallet } from "#/hooks/use-wallet";
import { TradePanel } from "../trade-panel.tsx";
import { BalancesTable } from "./balances-table.tsx";
import { FundingHistoryTable } from "./funding-history-table.tsx";
import { type PositionsState, usePositions } from "./hooks/use-positions.ts";
import { OpenOrdersTable } from "./open-orders-table.tsx";
import { OrderHistoryTable } from "./order-history-table.tsx";
import { PositionsTable } from "./positions-table.tsx";
import { PositionsTabs } from "./positions-tabs.tsx";
import { TradesTable } from "./trades-table.tsx";
import { TwapTable } from "./twap-table.tsx";

export function Positions() {
	const wallet = useWallet();
	const state = usePositions();

	return (
		<TradePanel className="overflow-hidden">
			<PositionsTabs {...state.tabs} />
			{wallet.isConnected ? (
				<PositionsTabContent state={state} />
			) : (
				<ConnectPrompt
					isAuthenticated={wallet.isAuthenticated}
					isReady={wallet.isReady}
					onConnect={() => wallet.connect("trade_cta")}
				/>
			)}
		</TradePanel>
	);
}

function PositionsTabContent({ state }: { state: PositionsState }) {
	const { tabs, data, actions } = state;
	const { errors } = data;

	switch (tabs.tab) {
		case "balances":
			return (
				<BalancesTable
					balances={data.balances}
					hideSmall={data.hideSmallBalances}
					isError={errors.balances.isError}
					onRetry={errors.balances.onRetry}
				/>
			);
		case "positions":
			return (
				<PositionsTable
					positions={data.filteredPositions}
					openOrders={data.openOrders}
					szDecimalsByCoin={data.szDecimalsByCoin}
					onNavigate={actions.onNavigate}
					onCloseMarket={actions.onCloseMarket}
					onCloseLimitSubmit={actions.onCloseLimitSubmit}
					onReverse={actions.onReverse}
					onCloseAll={actions.onCloseAll}
					onSetTpsl={actions.onSetTpsl}
					onCancelOrder={actions.onCancelOrder}
					isError={errors.positions.isError}
					onRetry={errors.positions.onRetry}
				/>
			);
		case "orders":
			return (
				<OpenOrdersTable
					openOrders={data.filteredOpenOrders}
					onCancelOrder={actions.onCancelOrderFromOpenOrders}
					onCancelAll={actions.onCancelAllOrders}
					onModifyOrder={actions.onModifyOrder}
					isError={errors.openOrders.isError}
					onRetry={errors.openOrders.onRetry}
				/>
			);
		case "twap":
			return (
				<TwapTable
					twapHistory={data.filteredTwapHistory}
					twapSliceFills={data.twapSliceFills}
					onCancelTwap={actions.onCancelTwap}
					isError={errors.twap.isError}
					onRetry={errors.twap.onRetry}
				/>
			);
		case "trade-history":
			return (
				<TradesTable
					fills={data.filteredFills}
					fetchNextPage={data.fetchNextPage}
					hasNextPage={data.hasNextPage}
					isFetchingNextPage={data.isFetchingNextPage}
					isError={errors.fills.isError}
					onRetry={errors.fills.onRetry}
				/>
			);
		case "funding-history":
			return (
				<FundingHistoryTable
					fundingHistory={data.filteredFundingHistory}
					fetchNextPage={data.fetchNextFundingPage}
					hasNextPage={data.hasNextFundingPage}
					isFetchingNextPage={data.isFetchingNextFundingPage}
					isError={errors.fundingHistory.isError}
					onRetry={errors.fundingHistory.onRetry}
				/>
			);
		case "order-history":
			return (
				<OrderHistoryTable
					orderHistory={data.filteredOrderHistory}
					isError={errors.orderHistory.isError}
					onRetry={errors.orderHistory.onRetry}
				/>
			);
	}
}

function ConnectPrompt({
	isAuthenticated,
	isReady,
	onConnect,
}: {
	isAuthenticated: boolean;
	isReady: boolean;
	onConnect: () => void;
}) {
	const { t } = useTranslate();
	return (
		<div className="flex flex-1 items-center justify-center py-8">
			{isAuthenticated ? (
				<span className="text-label2 text-muted-foreground">
					{t("state.loading-wallet")}
				</span>
			) : (
				<Button
					variant="ghost"
					size="sm"
					onClick={onConnect}
					disabled={!isReady}
					className="text-muted-foreground"
				>
					{t("positions.connect-prompt")}
				</Button>
			)}
		</div>
	);
}
